import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Row, Col, Modal, Alert } from "react-bootstrap";
import {
  createSubCategory,
  updateSubCategory,
  fetchSubCategoryById,
  clearError,
  clearSuccess,
} from "../../redux/slices/subCategorySlice";
import { fetchCategories } from "../../redux/slices/categorySlice";
import { toast } from "react-toastify";
import { FaRegTrashAlt } from "react-icons/fa";
const SubCategoryModal = ({
  show,
  handleClose,
  editId = null,
  categoryId = null,
}) => {
  const dispatch = useDispatch();
  const isEditMode = !!editId;

  console.log("editId-->", editId);
  console.log("categoryId-->", categoryId);

  const { isCreating, isUpdating, error, currentSubCategory, subCategories = [] } = useSelector(
    (state) => state.subCategories
  );

  const { categories = [] } = useSelector((state) => state.categories);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    image_url: "",
    category_id: "",
    status: "Active",
  });

  const [imageFile, setImageFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Load categories when modal opens (only if not already loaded)
  useEffect(() => {
    if (show && categories.length === 0) {
      dispatch(fetchCategories());
    }
  }, [dispatch, show, categories.length]);

  useEffect(() => {
    if (isEditMode && editId && show) {
      if (!currentSubCategory || currentSubCategory.id !== editId) {
        dispatch(fetchSubCategoryById(editId));
      }
    }
  }, [dispatch, editId, isEditMode, show, currentSubCategory]);

  // Populate form when currentSubCategory changes (for edit mode)
  useEffect(() => {
    if (isEditMode && currentSubCategory && currentSubCategory.id === editId) {
      setFormData({
        name: currentSubCategory.name || "",
        description: currentSubCategory.description || "",
        image_url: currentSubCategory.image_url || "",
        category_id: currentSubCategory.category_id || "",
        status: currentSubCategory.status || "Active",
      });
    }
  }, [currentSubCategory, isEditMode, editId]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      if (!isEditMode) {
        // Reset form for create mode and auto-fill category if provided
        resetForm();
        if (categoryId) {
          setFormData((prev) => ({ ...prev, category_id: categoryId }));
        }
      }
    } else {
      // Reset everything when modal closes
      resetForm();
    }
  }, [show, isEditMode, categoryId]);

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      image_url: "",
      category_id: "",
      status: "Active",
    });
    setImageFile(null);
    setValidationErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "name") {
      if (!/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
    }
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // File selection handler
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        e.target.value = "";
        return;
      }

      // Mandatory Dimension Validation: 2200 x 2000 Px
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        if (img.width !== 2200 || img.height !== 2000) {
          toast.error(`Image dimensions must be exactly 2200 x 2000 pixels. (Detected: ${img.width}x${img.height})`);
          setImageFile(null);
          e.target.value = "";
        } else {
          setImageFile(file);
        }
        URL.revokeObjectURL(objectUrl);
      };
      img.onerror = () => {
        toast.error("Failed to load image for validation");
        URL.revokeObjectURL(objectUrl);
        e.target.value = "";
      };
      img.src = objectUrl;
    } else {
      setImageFile(null);
    }
  };

  // Remove file handler
  const handleRemoveImage = () => {
    setImageFile(null);
    if (isEditMode) {
      setFormData((prev) => ({ ...prev, image_url: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Subcategory name is required";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
      errors.name = "Only alphabetic characters and spaces are allowed";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters long";
    }

    if (!formData.category_id) {
      errors.category_id = "Please select a category";
    }

    if (formData.description && formData.description.length > 500) {
      errors.description = "Description must not exceed 500 characters";
    }

    // Image is required in both create and update modes
    if (!imageFile && !formData.image_url) {
      errors.image = "Image is required";
    } else if (imageFile) {
      if (!imageFile.type.startsWith("image/")) {
        errors.image = "Please select a valid image file";
      } else if (imageFile.size > 5 * 1024 * 1024) {
        errors.image = "Image must be less than 5MB";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix validation errors");
      return;
    }



    try {
      setIsUploading(true);

      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        category_id: formData.category_id,
        status: formData.status,
      };

      if (isEditMode) {
        // Always include image_url in edit mode to preserve existing image or handle removal
        if (!imageFile) {
          // If no new file is uploaded, preserve the existing image_url (even if empty string)
          submitData.image_url = formData.image_url || null;
        }

        await dispatch(
          updateSubCategory({
            id: editId,
            subCategoryData: submitData,
            imageFile,
          })
        ).unwrap();
        // Toast will be shown by parent component based on Redux success state
      } else {
        await dispatch(
          createSubCategory({
            subCategoryData: submitData,
            imageFile,
          })
        ).unwrap();
        // Toast will be shown by parent component based on Redux success state
      }

      // Close modal immediately - parent will handle toast and refetch
      handleModalClose();
    } catch (err) {
      console.error("Subcategory operation failed:", err);
      toast.error(err || "Something went wrong");
    } finally {
      setIsUploading(false);
    }
  };

  const handleModalClose = () => {
    dispatch(clearError());
    // Don't clear success here - let parent component handle it and show toast
    // The parent will clear success after showing the toast
    handleClose();
  };

  const isSubmitting = isCreating || isUpdating || isUploading;

  return (
    <>
      <Modal show={show} onHide={handleModalClose} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {isEditMode ? `Update Subcategory` : "Create Subcategory"}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {error && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => dispatch(clearError())}
              >
                {error}
              </Alert>
            )}

            <Row className="mb-3">
              <Col md={6} className="ps-0">
                <Form.Group>
                  <Form.Label>
                    Subcategory Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    placeholder="Enter subcategory name"
                    value={formData.name}
                    onChange={handleInputChange}
                    isInvalid={!!validationErrors.name}
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6} className="pe-0">
                <Form.Group>
                  <Form.Label>
                    Category <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    isInvalid={!!validationErrors.category_id}
                    disabled={isSubmitting || (categoryId && !isEditMode)}
                  >
                    <option value="">Select Category</option>
                    {categories
                      .filter((category) => {
                        // Show active categories always
                        if (category.status === "Active") return true;
                        // In edit mode, also show the currently selected category even if inactive
                        if (
                          isEditMode &&
                          category.id === formData.category_id
                        ) {
                          return true;
                        }
                        return false;
                      })
                      .map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                          {category.status !== "Active" && " (Inactive)"}
                        </option>
                      ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.category_id}
                  </Form.Control.Feedback>
                  {/* {categoryId && !isEditMode && (
                    <Form.Text className="text-muted">
                      Category is automatically set from the parent category
                    </Form.Text>
                  )} */}
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12} className="p-0">
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* File upload field */}
            <Row className="mb-3">
              <Col md={12} className="p-0">
                <Form.Group>
                  <Form.Label>
                    Subcategory Image <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="mb-2 p-2 rounded bg-danger-soft border border-danger">
                    <p className="text-danger fw-bold mb-0 small">
                      <i className="fa-solid fa-triangle-exclamation me-2"></i>
                      IMPORTANT NOTE: Image must be exactly 2200 x 2000 pixels.
                    </p>
                  </div>
                  <Form.Control
                    id="file-uplad"
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    disabled={isSubmitting}
                    isInvalid={!!validationErrors.image}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.image}
                  </Form.Control.Feedback>

                  {/* Image preview section */}
                  {(imageFile || formData.image_url) && (
                    <div className="mt-2">
                      <div className="d-flex align-items-center gap-3">
                        <img
                          src={
                            imageFile
                              ? URL.createObjectURL(imageFile)
                              : formData.image_url
                          }
                          alt="Preview"
                          style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "cover",
                            borderRadius: "4px",
                          }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />

                        <div className="flex-grow-1">
                          <strong>
                            {imageFile ? imageFile.name : "Current Image"}
                          </strong>
                          {isEditMode && !imageFile && (
                            <div className="text-muted small">
                              Existing image will be kept
                            </div>
                          )}
                        </div>

                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={handleRemoveImage}
                          disabled={isSubmitting}
                        >
                          <FaRegTrashAlt />
                        </Button>
                      </div>
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                placeholder="Enter subcategory description (optional)"
                value={formData.description}
                onChange={handleInputChange}
                isInvalid={!!validationErrors.description}
                disabled={isSubmitting}
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.description}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                {formData.description.length}/500 characters
              </Form.Text>
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button
              className="btn-outline-danger-custom"
              onClick={handleModalClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className="btn-outline-success"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Processing..."
                : isEditMode
                ? "Update Subcategory"
                : "Create Subcategory"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default SubCategoryModal;
